"use client"

import { useQuery } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { getQueryTenantId } from "@/lib/auth/query-tenant-id"
import {
  getMetaAdsIntegrationAction,
  getMetaAdsOAuthAdAccountsAction,
  getMetaAdsOAuthPagesAction,
  getMetaAdsOAuthFormsAction,
  getMetaAdsSetupOptionsAction,
  getMetaAdsFollowUpOptionsAction,
  getMetaAdsFollowUpFormAction,
} from "@/server-actions/meta-ads"
import type { MetaAdsFormsReadiness } from "@/lib/meta-ads/types"

export const metaAdsIntegrationQueryKey = (tenantId: string) =>
  ["meta-ads-integration", tenantId] as const

export const metaAdsSetupOptionsQueryKey = (tenantId: string) =>
  ["meta-ads-setup-options", tenantId] as const

export function useMetaAdsFollowUpOptionsQuery(enabled: boolean) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getQueryTenantId(user)
  return useQuery({
    queryKey: ["meta-ads-follow-up-options", tenantId],
    enabled: isAuthenticated && enabled,
    queryFn: async () => {
      const result = await getMetaAdsFollowUpOptionsAction({})
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useMetaAdsFollowUpFormQuery(formId: string, enabled: boolean) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getQueryTenantId(user)
  return useQuery({
    queryKey: ["meta-ads-follow-up-form", tenantId, formId],
    enabled: isAuthenticated && enabled && Boolean(formId),
    queryFn: async () => {
      const result = await getMetaAdsFollowUpFormAction({ formId })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export const metaAdsOAuthPagesQueryKey = (tenantId: string, sessionId: string) =>
  ["meta-ads-oauth-pages", tenantId, sessionId] as const

export const metaAdsOAuthAdAccountsQueryKey = (tenantId: string, sessionId: string) =>
  ["meta-ads-oauth-ad-accounts", tenantId, sessionId] as const

export const metaAdsOAuthFormsQueryKey = (
  tenantId: string,
  sessionId: string,
  pageId: string,
) => ["meta-ads-oauth-forms", tenantId, sessionId, pageId] as const

export function useMetaAdsIntegrationQuery(enabled = true) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useQuery({
    queryKey: metaAdsIntegrationQueryKey(tenantId),
    enabled: isAuthenticated && enabled,
    refetchInterval: query => (
      query.state.data?.connection_health === "verification_pending" ? 2_000 : false
    ),
    queryFn: async () => {
      const result = await getMetaAdsIntegrationAction({})
      if (result && "errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useMetaAdsOAuthPagesQuery(sessionId: string | null) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useQuery({
    queryKey: metaAdsOAuthPagesQueryKey(tenantId, sessionId ?? ""),
    enabled: isAuthenticated && Boolean(sessionId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!sessionId) return []
      const result = await getMetaAdsOAuthPagesAction({ sessionId })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useMetaAdsOAuthAdAccountsQuery(sessionId: string | null) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useQuery({
    queryKey: metaAdsOAuthAdAccountsQueryKey(tenantId, sessionId ?? ""),
    enabled: isAuthenticated && Boolean(sessionId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!sessionId) return []
      const result = await getMetaAdsOAuthAdAccountsAction({ sessionId })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useMetaAdsSetupOptionsQuery(enabled: boolean) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useQuery({
    queryKey: metaAdsSetupOptionsQueryKey(tenantId),
    enabled: isAuthenticated && enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const result = await getMetaAdsSetupOptionsAction({})
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useMetaAdsOAuthFormsQuery(sessionId: string | null, pageId: string) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useQuery({
    queryKey: metaAdsOAuthFormsQueryKey(tenantId, sessionId ?? "", pageId),
    enabled: isAuthenticated && Boolean(sessionId) && Boolean(pageId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!sessionId || !pageId) return EMPTY_META_ADS_FORMS_READINESS
      const result = await getMetaAdsOAuthFormsAction({ sessionId, pageId })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

const EMPTY_META_ADS_FORMS_READINESS: MetaAdsFormsReadiness = {
  forms: [],
  leadAccessStatus: "unverified",
  leadAccessErrorCode: null,
}
